import { concat, eq, findIndex, get, isUndefined, omit, reject, sortBy } from 'lodash';
import { ITaxonomyTerms } from '@kentico/kontent-delivery';

import { createDeliveryClient } from 'api/helpers';
import logger from 'utils/logger';
import { COUNTRY, envUtil } from 'common/util';
import { EMPTY_STRING } from '../../common/utils/string';

export type FilterTaxonomy = FilterTaxonomyOptions & {
  elements: {
    audience: FilterTaxonomyItem;
    occasion: FilterTaxonomyItem;
    interest: FilterTaxonomyItem;
    location: FilterTaxonomyItem;
  };
};

export type FilterTaxonomyOptions = {
  root: {
    title: string;
    clearAll: {
      title: string;
    };
    close: {
      title: string;
    };
    viewResults: {
      title: string;
    };
  };
  filter: {
    title: string;
    options: {
      audience: {
        search: {
          title: string;
        };
      };
      occasion: {
        search: {
          title: string;
        };
      };
      interest: {
        search: {
          title: string;
        };
      };
      location: {
        search: {
          title: string;
        };
      };
    };
  };
  search: {
    title: string;
  };
  sort: {
    title: string;
    switched: boolean;
    options: {
      literalUp: {
        title: string;
      };
      literalDown: {
        title: string;
      };
      featured: {
        title: string;
      };
      newArrivals: {
        title: string;
      };
    };
  };
};

/**
 * Filter Taxonomy Options from the Filter Taxonomy object
 * @param filterList
 */
export function getFilterTaxonomyOptions(filterList: FilterTaxonomy): FilterTaxonomyOptions {
  return omit(filterList, 'elements');
}

export type FilterTaxonomyServer = {
  audience: FilterTaxonomyItem;
  occasion: FilterTaxonomyItem;
  interest: FilterTaxonomyItem;
  locationAu: FilterTaxonomyItem;
  locationUk: FilterTaxonomyItem;
  locationNz: FilterTaxonomyItem;
  locationUs: FilterTaxonomyItem;
};

export type FilterTaxonomyItem = {
  name: string;
  question?: string;
  switched?: boolean;
  terms: ITaxonomyTerms[];
};

const DEFAULT_ROOT_SECTION_TITLE = `Let's find the perfect gift`;

function withNational(terms: ITaxonomyTerms[]): ITaxonomyTerms[] {
  const NATIONAL_CODENAME = 'national_or_online';
  const nationalValue = terms[findIndex(terms, ['codename', NATIONAL_CODENAME])];
  return !isUndefined(nationalValue) ? concat([nationalValue], reject(terms, { codename: NATIONAL_CODENAME })) : terms;
}

function getLocationFilterList(options: FilterTaxonomyOptions, filterList: FilterTaxonomyServer): FilterTaxonomy {
  const currentCounty = envUtil.getCountry();
  return {
    root: {
      title: get(options, 'root.title', DEFAULT_ROOT_SECTION_TITLE), // @note:@todo: || defaultFilterListValue('root.title'),
      clearAll: {
        title: get(options, 'root.clearAll.title', 'Clear All'),
      },
      close: {
        title: get(options, 'root.close.title', 'Close'),
      },
      viewResults: {
        title: get(options, 'root.viewResults.title', 'View Results'),
      },
    },
    search: {
      title: get(options, 'search.title', 'Search Brands'),
    },
    filter: {
      title: get(options, 'filter.title', 'Filter'),
      options: {
        interest: {
          search: {
            title: get(options, 'filter.options.interest.search.title', `What's their interests?`),
          },
        },
        location: {
          search: {
            title: get(options, 'filter.options.location.search.title', `Search State/Province`),
          },
        },
        audience: {
          search: {
            title: get(options, 'filter.options.audience.search.title', `Who is it for?`),
          },
        },
        occasion: {
          search: {
            title: get(options, 'filter.options.occasion.search.title', `What's the occasion?`),
          },
        },
      },
    },
    sort: {
      title: get(options, 'sort.title', 'Sort By'),
      switched: get(options, 'sort.switched', false),
      options: get(options, 'sort.options', void 0),
    },
    elements: {
      audience: {
        ...filterList.audience,
        terms: sortBy(filterList.audience.terms, ({ name }) => name),
      }, // @todo: composeElement(filterList, 'audience'),
      occasion: {
        ...filterList.occasion,
        terms: sortBy(filterList.occasion.terms, ({ name }) => name),
      },
      interest: {
        ...filterList.interest,
        terms: sortBy(filterList.interest.terms, ({ name }) => name),
      },
      location: {
        [COUNTRY.Australia]: {
          ...filterList.locationAu,
          terms: withNational(sortBy(filterList.locationAu.terms, ({ name }) => name)),
        },
        [COUNTRY.GreatBritain]: {
          ...filterList.locationUk,
          terms: withNational(sortBy(filterList.locationUk.terms, ({ name }) => name)),
        },
        [COUNTRY.UnitedStates]: {
          ...filterList.locationUs,
          terms: withNational(sortBy(filterList.locationUs.terms, ({ name }) => name)),
        },
        [COUNTRY.NewZealand]: {
          ...filterList.locationNz,
          terms: withNational(sortBy(filterList.locationNz.terms, ({ name }) => name)),
        },
      }[currentCounty],
    },
  };
}

async function getLocationStore(language: string, isPreview: boolean): Promise<any> {
  const client = createDeliveryClient(language, isPreview);
  const [storeAu, storeNz, storeUk, storeUs] = await Promise.all([
    client.item('prezzee_classic_store_au').toPromise(),
    client.item('prezzee_classic_store_nz').toPromise(),
    client.item('prezzee_classic_store_uk').toPromise(),
    client.item('prezzee_classic_store_us').toPromise(),
  ]);
  const currentCountry = envUtil.getCountry();
  return {
    [COUNTRY.Australia]: storeAu.data,
    [COUNTRY.GreatBritain]: storeUk.data,
    [COUNTRY.UnitedStates]: storeUs.data,
    [COUNTRY.NewZealand]: storeNz.data,
  }[currentCountry];
}

// @todo: compose the sort options mapper
export function getSortOptionFromStore(store: any): any {
  return store.item.elemenets.sort_options.value;
}

/**
 * Returns the default Store Content type element value
 * @note: the localisation can be provided
 * @param path
 */
function defaultStoreValue(path: string): string | number {
  return {
    view_results_title: 'View Results',
    close_title: 'Close',
    clear_all_title: 'Clear All',
  }[path];
}

const DEFAULT_SWITCHED_ON_FILTER_VALUE = 'on';

export function composeFilterListOptions(store: any): FilterTaxonomyOptions {
  return {
    root: {
      title: get(store, 'item.elements.title.value', EMPTY_STRING),
      viewResults: {
        title: get(store, 'item.elements.view_results_title.value', defaultStoreValue('view_results_title')),
      },
      close: {
        title: get(store, 'item.elements.close_title.value', defaultStoreValue('close_title')),
      },
      clearAll: {
        title: get(store, 'item.elements.clear_all_title.value', defaultStoreValue('clear_all_title')),
      },
    },
    sort: {
      title: get(store, 'item.elements.sort_placeholder.value', void 0),
      switched: eq(
        get(store, 'item.elements.sort_by_on_off.value[0].codename', void 0),
        DEFAULT_SWITCHED_ON_FILTER_VALUE
      ),
      // @todo: the cms items management
      options: {
        newArrivals: {
          title: 'New Arrivals',
        },
        featured: {
          title: 'Featured',
        },
        literalDown: {
          title: 'Z - A',
        },
        literalUp: {
          title: 'A - Z',
        },
      }, // getSortOptionFromStore(store), // @todo: compose the options CMS value
    },
    filter: {
      title: get(store, 'item.elements.filter_placeholder.value', void 0),
      options: {
        interest: {
          search: {
            title: get(store, 'item.elements.interests_search_placeholder.value', `What's their interests?`),
          },
        },
        location: {
          search: {
            title: get(store, 'item.elements.location_search_placeholder.value', `What's their interests?`),
          },
        },
        audience: {
          search: {
            title: get(store, 'item.elements.audience_search_placeholder.value', `What's their interests?`),
          },
        },
        occasion: {
          search: {
            title: get(store, 'item.elements.occasion_search_placeholder.value', `What's their interests?`),
          },
        },
      },
    },
    search: {
      title: get(store, 'item.elements.search_placeholder.value', void 0),
    },
  };
}

// @todo: How to log the catch or then response local?
const getFilterList = async (language: string, isPreview: boolean): Promise<FilterTaxonomy> => {
  try {
    const client = createDeliveryClient(language, isPreview);
    const [interest, audience, occasion, locationAu, locationNz, locationUk, locationUs] = await Promise.all([
      // @todo: optimise location queries
      client.taxonomy('interest').toPromise(),
      client.taxonomy('audience').toPromise(),
      client.taxonomy('occasion').toPromise(),
      client.taxonomy('location___au').toPromise(),
      client.taxonomy('location___nz').toPromise(),
      client.taxonomy('location___uk').toPromise(),
      client.taxonomy('location___us').toPromise(),
    ]);
    const store = await getLocationStore('100-en-US', isPreview); // @note: use the default language for the Store content item
    const list = getLocationFilterList(composeFilterListOptions(store), {
      interest: {
        name: 'Interest',
        switched: store.item.elements.interests_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.interests_filter_title.value, // `What's their interests?`,
        terms: interest.data.taxonomy.terms,
      },
      audience: {
        name: 'Audience',
        switched: store.item.elements.audience_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.audience_filter_title.value, // 'Who is it for?',
        terms: audience.data.taxonomy.terms,
      },
      occasion: {
        name: 'Occasion',
        switched: store.item.elements.occasion_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.occasion_filter_title.value, // `What's the occasion?`,
        terms: occasion.data.taxonomy.terms,
      },
      locationAu: {
        name: 'Location AU',
        switched: store.item.elements.location_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.location_filter_title.value, // `Where are they from?`,
        terms: locationAu.data.taxonomy.terms,
      },
      locationNz: {
        name: 'Location NZ',
        switched: store.item.elements.location_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.location_filter_title.value,
        terms: locationNz.data.taxonomy.terms,
      },
      locationUk: {
        name: 'Location UK',
        switched: store.item.elements.location_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.location_filter_title.value,
        terms: locationUk.data.taxonomy.terms,
      },
      locationUs: {
        name: 'Location US',
        switched: store.item.elements.location_on_off.value[0]?.codename === DEFAULT_SWITCHED_ON_FILTER_VALUE,
        question: store.item.elements.location_filter_title.value,
        terms: locationUs.data.taxonomy.terms,
      },
    });
    return list;
  } catch (e) {
    // datadogLogs.logger.info('getFilterList error: ', e);
    logger.info(
      {
        err: e,
        source: 'Gift Finder',
        journey: 'Gift Open',
      },
      'error: Failed to fetch Gift Finder taxonomies'
    );
    console.log('getFilterList error: ', e);
    return null;
  }

  // const conlog = new Logger(
  //   (logsMessage: LogsMessage, logger: Logger) =>
  //     logger.log(logsMessage.message, logsMessage)
  // );
  //
  // conlog.log('defaultTaxonomy: ', defaultTaxonomy);
};

export default getFilterList;
